import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface OrgMembership {
  org_id: string;
  org_name: string;
  org_slug: string;
  role: string;
  plan_tier: string;
}

interface OrgContextValue {
  /** The currently active organization ID */
  activeOrgId: string | null;
  /** All orgs the user belongs to */
  memberships: OrgMembership[];
  /** Switch to a different org */
  switchOrg: (orgId: string) => void;
  /** Whether the org data is still loading */
  loading: boolean;
  /** The active org's details */
  activeOrg: OrgMembership | null;
}

const OrgContext = createContext<OrgContextValue>({
  activeOrgId: null,
  memberships: [],
  switchOrg: () => {},
  loading: true,
  activeOrg: null,
});

export const useOrg = () => useContext(OrgContext);

const ACTIVE_ORG_KEY = "struvent_active_org";

export const OrgProvider = ({ children }: { children: React.ReactNode }) => {
  const [memberships, setMemberships] = useState<OrgMembership[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMemberships = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from("organization_members")
      .select("org_id, role, organizations(id, name, slug, plan_tier)")
      .eq("user_id", userId);

    if (error || !data) {
      console.error("Failed to load org memberships:", error);
      setLoading(false);
      return;
    }

    const mapped: OrgMembership[] = data
      .filter((m: any) => m.organizations)
      .map((m: any) => ({
        org_id: m.org_id,
        role: m.role,
        org_name: m.organizations.name,
        org_slug: m.organizations.slug,
        plan_tier: m.organizations.plan_tier,
      }));

    setMemberships(mapped);

    // Restore previously selected org, or default to first
    const stored = localStorage.getItem(ACTIVE_ORG_KEY);
    const validStored = mapped.find((m) => m.org_id === stored);
    const selected = validStored ? stored! : mapped[0]?.org_id ?? null;
    setActiveOrgId(selected);
    if (selected) localStorage.setItem(ACTIVE_ORG_KEY, selected);

    setLoading(false);
  }, []);

  useEffect(() => {
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (session?.user) {
          await loadMemberships(session.user.id);
        } else {
          setMemberships([]);
          setActiveOrgId(null);
          setLoading(false);
        }
      }
    );

    // Initial load
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        loadMemberships(user.id);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadMemberships]);

  const switchOrg = useCallback(
    (orgId: string) => {
      const valid = memberships.find((m) => m.org_id === orgId);
      if (valid) {
        setActiveOrgId(orgId);
        localStorage.setItem(ACTIVE_ORG_KEY, orgId);
      }
    },
    [memberships]
  );

  const activeOrg = memberships.find((m) => m.org_id === activeOrgId) ?? null;

  return (
    <OrgContext.Provider
      value={{ activeOrgId, memberships, switchOrg, loading, activeOrg }}
    >
      {children}
    </OrgContext.Provider>
  );
};
